import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useApiFetch } from "../api/apiClient";
import { colors } from "../utils/styles";
import { logError } from "../utils/logger";
import { fmtDateTime } from "../utils/dateFormat";
import { getSubmissionYear, yearOptionsFrom } from "../utils/submission";

const COLUMN_COUNT = 14;

const wrapCellsSx = {
  "& .MuiTableCell-root": { wordBreak: "break-word", verticalAlign: "top" }
};

// Single source of truth for the Agency cell label and the agency filter
// dedupe key — kept in sync so the dropdown options match what the row shows.
function agencyLabel(row) {
  if (row.groupName) return row.groupName;
  if (row.parentJurisdictionCode) {
    return `${row.parentJurisdictionName || "—"} (${row.parentJurisdictionCode})`;
  }
  return "—";
}

export default function AdminSubmissions() {
  const apiFetch = useApiFetch();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [detailsById, setDetailsById] = useState({}); // { [id]: { loading, error, detail } }
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [yearFilters, setYearFilters] = useState([]);
  const [pdfViewer, setPdfViewer] = useState(null); // { id, url } | null
  const [agencyFilters, setAgencyFilters] = useState([]);
  const [subagencyFilters, setSubagencyFilters] = useState([]);

  const yearOptions = useMemo(
    () => yearOptionsFrom((rows || []).map((r) => getSubmissionYear(r.submissionIdentifier))),
    [rows]
  );

  const agencyOptions = useMemo(() => {
    if (!rows) return [];
    const seen = new Map();
    for (const r of rows) {
      const label = agencyLabel(r);
      if (label === "—") continue;
      if (!seen.has(label)) seen.set(label, { key: label, label });
    }
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  // Cascades off the agency filter: when an agency is selected, only the
  // subagencies that appear in that agency's submissions are offered.
  const subagencyOptions = useMemo(() => {
    if (!rows) return [];
    const agencyLabels = agencyFilters.length ? new Set(agencyFilters.map((a) => a.label)) : null;
    const seen = new Map();
    for (const r of rows) {
      if (agencyLabels && !agencyLabels.has(agencyLabel(r))) continue;
      for (const sa of r.subagencies || []) {
        if (!sa.jurisdictionCode || seen.has(sa.jurisdictionCode)) continue;
        seen.set(sa.jurisdictionCode, {
          code: sa.jurisdictionCode,
          name: sa.jurisdictionName || null,
          label: `${sa.jurisdictionName || sa.jurisdictionCode} (${sa.jurisdictionCode})`
        });
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [rows, agencyFilters]);

  // Drops any subagency selections that no longer belong to the selected
  // agency set, without re-deriving eligibility separately from subagencyOptions.
  useEffect(() => {
    setSubagencyFilters((prev) => {
      if (prev.length === 0) return prev;
      const validCodes = new Set(subagencyOptions.map((o) => o.code));
      const next = prev.filter((sf) => validCodes.has(sf.code));
      return next.length === prev.length ? prev : next;
    });
  }, [subagencyOptions]);

  const filteredRows = useMemo(() => {
    if (!rows) return [];
    if (!yearFilters.length && !agencyFilters.length && !subagencyFilters.length) return rows;
    const yearSet = yearFilters.length ? new Set(yearFilters) : null;
    const agencySet = agencyFilters.length ? new Set(agencyFilters.map((a) => a.label)) : null;
    const subagencySet = subagencyFilters.length ? new Set(subagencyFilters.map((s) => s.code)) : null;
    return rows.filter((r) => {
      if (yearSet && !yearSet.has(getSubmissionYear(r.submissionIdentifier))) return false;
      if (agencySet && !agencySet.has(agencyLabel(r))) return false;
      if (subagencySet && !(r.subagencies || []).some((sa) => subagencySet.has(sa.jurisdictionCode))) return false;
      return true;
    });
  }, [rows, yearFilters, agencyFilters, subagencyFilters]);

  useEffect(() => {
    let cancelled = false;
    apiFetch("/rm/preliminary-notification/all")
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setRows(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to load submissions.");
      });
    return () => {
      cancelled = true;
    };
  }, [apiFetch]);

  const fetchDetail = useCallback(
    async (id) => {
      setDetailsById((prev) => ({ ...prev, [id]: { loading: true } }));
      try {
        const res = await apiFetch(`/rm/preliminary-notification/by-id/${encodeURIComponent(id)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const detail = await res.json();
        setDetailsById((prev) => ({ ...prev, [id]: { loading: false, detail } }));
      } catch (err) {
        setDetailsById((prev) => ({ ...prev, [id]: { loading: false, error: err.message } }));
      }
    },
    [apiFetch]
  );

  const openPdf = useCallback(
    async (id) => {
      try {
        const res = await apiFetch(`/rm/preliminary-notification/by-id/${encodeURIComponent(id)}/pdf`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        setPdfViewer({ id, url: URL.createObjectURL(blob) });
      } catch (err) {
        logError("Failed to open submission PDF:", err);
      }
    },
    [apiFetch]
  );

  const closePdfViewer = useCallback(() => setPdfViewer(null), []);

  // Revokes the blob URL when the viewer changes or unmounts. Owns the full
  // lifecycle so openPdf/closePdfViewer don't need to revoke themselves.
  useEffect(() => {
    if (!pdfViewer) return undefined;
    return () => URL.revokeObjectURL(pdfViewer.url);
  }, [pdfViewer]);

  const toggleRow = useCallback(
    (id) => {
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
          if (!detailsById[id]) fetchDetail(id);
        }
        return next;
      });
    },
    [detailsById, fetchDetail]
  );

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        Submissions
      </Typography>

      {error && (
        <Box
          sx={{ p: 2, mb: 2, bgcolor: colors.errorBg, color: colors.error, borderRadius: 1, fontSize: 13 }}
        >
          Failed to load submissions: {error}
        </Box>
      )}

      {!error && rows === null && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {rows && rows.length === 0 && (
        <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>No submissions yet.</Box>
      )}

      {rows && rows.length > 0 && (
        <>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ mb: 2, alignItems: { sm: "center" } }}
          >
            <Autocomplete
              multiple
              disableCloseOnSelect
              limitTags={1}
              options={yearOptions}
              value={yearFilters}
              onChange={(_, v) => {
                setYearFilters(v);
                setPage(0);
              }}
              sx={{ minWidth: 200, flex: 0 }}
              renderInput={(params) => <TextField {...params} label="Year" size="small" />}
            />
            <Autocomplete
              multiple
              disableCloseOnSelect
              limitTags={1}
              options={agencyOptions}
              value={agencyFilters}
              onChange={(_, v) => {
                setAgencyFilters(v);
                setPage(0);
              }}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.key === v.key}
              sx={{ minWidth: 320, flex: 1 }}
              renderInput={(params) => <TextField {...params} label="Agency/Group" size="small" />}
            />
            <Autocomplete
              multiple
              disableCloseOnSelect
              limitTags={1}
              options={subagencyOptions}
              value={subagencyFilters}
              onChange={(_, v) => {
                setSubagencyFilters(v);
                setPage(0);
              }}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              sx={{ minWidth: 320, flex: 1 }}
              renderInput={(params) => <TextField {...params} label="Subagency" size="small" />}
            />
            {(yearFilters.length > 0 || agencyFilters.length > 0 || subagencyFilters.length > 0) && (
              <Button
                onClick={() => {
                  setYearFilters([]);
                  setAgencyFilters([]);
                  setSubagencyFilters([]);
                  setPage(0);
                }}
              >
                Clear filters
              </Button>
            )}
          </Stack>

          {(yearFilters.length > 0 || agencyFilters.length > 0 || subagencyFilters.length > 0) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Showing {filteredRows.length} of {rows.length} submission{rows.length === 1 ? "" : "s"}.
            </Typography>
          )}
        </>
      )}

      {rows && rows.length > 0 && filteredRows.length === 0 && (
        <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
          No submissions match the current filters.
        </Box>
      )}

      {rows && rows.length > 0 && filteredRows.length > 0 && (
        <TableContainer component={Paper}>
          <Table stickyHeader size="small" sx={wrapCellsSx}>
            <TableHead
              sx={{
                "& .MuiTableCell-head": {
                  bgcolor: colors.darkGray,
                  color: "white",
                  fontWeight: 700
                }
              }}
            >
              <TableRow>
                <TableCell sx={{ width: 36 }} />
                <TableCell>Submission ID</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>By</TableCell>
                <TableCell>Parent Code</TableCell>
                <TableCell>Parent Name</TableCell>
                <TableCell>Agreement Required</TableCell>
                <TableCell>Group Name</TableCell>
                <TableCell>Group Address</TableCell>
                <TableCell>Group Agreement Required</TableCell>
                <TableCell>Preparer</TableCell>
                <TableCell align="right">Subagencies</TableCell>
                <TableCell align="right">Signers</TableCell>
                <TableCell align="center">Submission PDF</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((r) => {
                const isOpen = expanded.has(r.submissionIdentifier);
                const detailState = detailsById[r.submissionIdentifier];
                return (
                  <RowWithDetail
                    key={r.submissionIdentifier}
                    row={r}
                    open={isOpen}
                    onToggle={() => toggleRow(r.submissionIdentifier)}
                    onOpenPdf={() => openPdf(r.submissionIdentifier)}
                    detailState={detailState}
                  />
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {rows && rows.length > 0 && filteredRows.length > 0 && (
        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 25, 50, 100]}
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      )}

      <Dialog open={!!pdfViewer} onClose={closePdfViewer} fullWidth maxWidth="lg">
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: "monospace" }}>
            {pdfViewer?.id}
          </Typography>
          <IconButton onClick={closePdfViewer} size="small" aria-label="Close PDF viewer">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: "85vh" }}>
          {pdfViewer && (
            <iframe
              src={pdfViewer.url}
              title="Submission PDF"
              style={{ width: "100%", height: "100%", border: 0, display: "block" }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function RowWithDetail({ row, open, onToggle, onOpenPdf, detailState }) {
  return (
    <>
      <TableRow hover sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton size="small" onClick={onToggle} aria-label={open ? "Collapse" : "Expand"}>
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontFamily: "monospace" }}>{row.submissionIdentifier}</TableCell>
        <TableCell>{fmtDateTime(row.submittedAt)}</TableCell>
        <TableCell>{row.submittedByEmail}</TableCell>
        <TableCell sx={{ fontFamily: "monospace" }}>{row.parentJurisdictionCode || "—"}</TableCell>
        <TableCell>{row.parentJurisdictionName || "—"}</TableCell>
        <TableCell>{row.agreementReq || "—"}</TableCell>
        <TableCell>{row.groupName || "—"}</TableCell>
        <TableCell sx={{ maxWidth: 240, whiteSpace: "normal" }}>{row.groupAddress || "—"}</TableCell>
        <TableCell>{row.groupAgreementRequired || "—"}</TableCell>
        <TableCell>
          {row.formPreparerName}
          <Typography variant="caption" display="block" color="text.secondary">
            {row.formPreparerEmail}
          </Typography>
          {row.formPreparerPhone && (
            <Typography variant="caption" display="block" color="text.secondary">
              {row.formPreparerPhone}
            </Typography>
          )}
        </TableCell>
        <TableCell align="right">{row.subagencyCount}</TableCell>
        <TableCell align="right">{row.signerCount}</TableCell>
        <TableCell align="center">
          {row.hasSubmissionPdf && (
            <IconButton size="small" onClick={onOpenPdf} aria-label="Open submission PDF">
              <PictureAsPdfIcon fontSize="small" />
            </IconButton>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ pb: 0, pt: 0 }} colSpan={COLUMN_COUNT}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 1 }}>
              {detailState?.loading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {detailState?.error && (
                <Box sx={{ color: colors.error, fontSize: 13 }}>Failed to load: {detailState.error}</Box>
              )}
              {detailState?.detail && <DetailPanel detail={detailState.detail} />}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function DetailPanel({ detail }) {
  const { signers = [], subagencies = [] } = detail;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Section title={`Signers (${signers.length})`}>
        {signers.length === 0 ? (
          <Empty>No signers</Empty>
        ) : (
          <Table size="small" sx={wrapCellsSx}>
            <TableHead sx={{ "& .MuiTableCell-head": { bgcolor: colors.lightGray, fontWeight: 700 } }}>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Agreement Type</TableCell>
                <TableCell>Agreement Jurisdiction Name</TableCell>
                <TableCell>Group Agreement Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {signers.map((s, i) => (
                <TableRow key={i}>
                  <TableCell>{s.name || "—"}</TableCell>
                  <TableCell>{s.title || "—"}</TableCell>
                  <TableCell>{s.phone || "—"}</TableCell>
                  <TableCell>{s.email || "—"}</TableCell>
                  <TableCell>{s.agreementType || "—"}</TableCell>
                  <TableCell>{s.agreementJurisdictionName || "—"}</TableCell>
                  <TableCell>{s.groupAgreementType || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      <Section title={`Subagencies (${subagencies.length})`}>
        {subagencies.length === 0 ? (
          <Empty>No subagencies</Empty>
        ) : (
          <TableContainer sx={{ maxHeight: 300 }}>
            <Table size="small" stickyHeader sx={wrapCellsSx}>
              <TableHead sx={{ "& .MuiTableCell-head": { bgcolor: colors.lightGray, fontWeight: 700 } }}>
                <TableRow>
                  <TableCell>Subagency</TableCell>
                  <TableCell>Account Type</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>New Tax Bill Desc</TableCell>
                  <TableCell>Charge Status</TableCell>
                  <TableCell>Charge Category</TableCell>
                  <TableCell>Service Description</TableCell>
                  <TableCell align="right">Parcels</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Statute Code</TableCell>
                  <TableCell>Legal Authority Type</TableCell>
                  <TableCell>Approval Method</TableCell>
                  <TableCell>Election Date</TableCell>
                  <TableCell>Approval Date</TableCell>
                  <TableCell>Adoption Type</TableCell>
                  <TableCell>Adoption Date</TableCell>
                  <TableCell>Original Adoption Date</TableCell>
                  <TableCell>Legal Authority Current</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subagencies.map((sa, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Typography variant="body2">{sa.jurisdictionName || "—"}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                        {sa.jurisdictionCode}
                      </Typography>
                    </TableCell>
                    <TableCell>{sa.accountType}</TableCell>
                    <TableCell>{sa.isAccountActive ? "Yes" : "No"}</TableCell>
                    <TableCell>{sa.newTaxBillDescription || "—"}</TableCell>
                    <TableCell>{sa.chargeStatus}</TableCell>
                    <TableCell>{sa.chargeCategory}</TableCell>
                    <TableCell sx={{ maxWidth: 280, whiteSpace: "normal" }}>
                      {sa.serviceDescription}
                    </TableCell>
                    <TableCell align="right">{sa.estimatedParcels}</TableCell>
                    <TableCell align="right">
                      {typeof sa.estimatedAmount === "number"
                        ? sa.estimatedAmount.toLocaleString("en-US", { style: "currency", currency: "USD" })
                        : sa.estimatedAmount}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280, whiteSpace: "normal" }}>{sa.statuteCode}</TableCell>
                    <TableCell>{sa.legalAuthorityType}</TableCell>
                    <TableCell>{sa.approvalMethod}</TableCell>
                    <TableCell>{sa.electionDate || "—"}</TableCell>
                    <TableCell>{sa.approvalDate || "—"}</TableCell>
                    <TableCell>{sa.adoptionType || "—"}</TableCell>
                    <TableCell>{sa.adoptionDate || "—"}</TableCell>
                    <TableCell>{sa.originalAdoptionDate || "—"}</TableCell>
                    <TableCell>{sa.isLegalAuthorityCurrent ? "Yes" : "No"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Section>
    </Box>
  );
}

function Section({ title, children }) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "text.secondary" }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function Empty({ children }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
      {children}
    </Typography>
  );
}
